import { defineStore } from 'pinia';
import _ from 'lodash'
import dayjs from 'dayjs'
import { discountItemLineFn, calculateItems, calculateBilling } from '~/class/TableHelper'
import { JobMode } from '~/interface/Job'
import { activeSkipServiceResolver } from '~/utils/billingResolvers'
export const usePosCartItemsStore = defineStore('cartItem', {
  state: () => ({
    items: [],
    deliveryInfo: null
  }),

  getters: {
    itemCount: (state) => {
      return state.items.reduce((total, item) => total + Number(item.quantity), 0);
    },
    cartTotal: (state) => {
      return state.items.reduce((total, item) => {
        let itemTotal = item.price * item.quantity;
        let toppingTotal = 0;
        let discountTotal = 0

        if (item?.toppings?.length > 0) {
          toppingTotal = item.toppings.reduce((_total, topping) => _total + ((topping.price * topping.quantity) * item.quantity), 0);
        }

        // Prefer the live discountrequest (mode/value) — compute the display
        // discount with the same canonical calculator the job/bill uses so the
        // subtotal shown to the cashier matches the final receipt.
        if (item?.discountrequest && Number(item.discountrequest.value)) {
          discountTotal = discountItemLineFn(
            itemTotal + toppingTotal,
            Number(item.quantity) || 0,
            Number(item.discountrequest.mode) || 0,
            Number(item.discountrequest.value) || 0
          )
        } else if (item?.discount) {
          discountTotal = item.discount[2]
        }

        return total + (itemTotal) + toppingTotal - discountTotal;
      }, 0);
    },
    // Live billing for what is currently in the cart — Service Charge, VAT and
    // Grand Total, not just the item sum in `cartTotal`. Terminal devices only
    // ever saw the raw item sum, which reads far lower than the real bill.
    //
    // Routed through the SAME canonical pair the table/receipt uses
    // (calculateItems -> calculateBilling), against the target table's own
    // options so a per-table SC/VAT override is honoured.
    //
    // calculateItems speaks the ORDER-LINE shape (all-lowercase keys, as stored
    // in the jobchain) while cart lines are camelCase, so both differing keys are
    // normalised on the way in: `discountRequest` vs `discountrequest`, and
    // `subitemid` vs `subItemId`. The latter is not cosmetic — every resolver is
    // keyed by sub-item id, so leaving it unmapped silently passed `undefined`
    // and every tag came back false.
    //
    // Note: no noDiscount resolver here — the "% excluding no-discount lines"
    // dc2 mode (2) is a whole-bill discount applied at payment, which a terminal
    // cart never sets.
    //
    // skipService IS passed, unlike noDiscount: on a strategy that reads it the
    // tag moves the service-charge and exclusive-VAT bases, both of which this
    // getter computes. Omitting it made the cart quote a higher SC/VAT than the
    // table card charged for the same lines. It resolves to undefined (zero
    // per-line cost) on any strategy that ignores the tag.
    cartBilling: (state) => {
      const cartStore = usePosCartStore()
      const { tableBillingGlobal, tablesState } = usePosTableStatesStore()
      const settingsStore = useSettingsStore()

      const normalised = state.items.map((line: any) => ({
        ...line,
        discountRequest: line.discountRequest || line.discountrequest,
        subitemid: line.subitemid ?? line.subItemId,
      }))

      const noServiceCharge = cartStore.mode === JobMode.TOGO && settingsStore.togoNoService
      return calculateBilling(
        calculateItems(normalised, undefined, activeSkipServiceResolver()),
        tableBillingGlobal,
        tablesState[cartStore.tableName],
        { noServiceCharge },
      )
    },
    /**
     * True when a cart line is exempt from service charge / exclusive VAT under
     * the shop's billing strategy — the same `skipService` tag calculateItems
     * stamps, exposed so the cart can label the line.
     *
     * Resolved through the identical resolver cartBilling uses, from the same
     * sub-item id, so the badge can never disagree with the Grand Total below it.
     * Always false on a strategy that ignores the tag, so no other shop sees it.
     */
    isSkipServiceLine: () => {
      const resolve = activeSkipServiceResolver()
      if (!resolve) return () => false
      // Cart lines are camelCase; order lines are all-lowercase. Accept both.
      return (line: any) => !!resolve(line?.subitemid ?? line?.subItemId)
    },
    subItemIdMapQty: (state) => {
      return state.items.reduce((total, item) => {
        total[item.parentId] = (total[item.parentId] || 0) + item.quantity
        return total
      }, {})
    },
    // Quantity keyed by the actual SubItem (SKU) id — e.g. Coke S and Coke L
    // are counted separately, unlike subItemIdMapQty which sums per parent item.
    qtyBySubItemId: (state) => {
      return state.items.reduce((total, item) => {
        total[item.subItemId] = (total[item.subItemId] || 0) + item.quantity
        return total
      }, {})
    },
  },

  actions: {



    setDeliveryInfo(deliveryInfo) {
      this.deliveryInfo = deliveryInfo
    },





    //_items is subitem
    handleAddItemToCart(_item, options: { parentItem?: any, toppings?: any, comments?: string, isOpenMenu?: boolean, openMenuName?: string }) {



      let name = options?.parentItem?.Name

      //WHEN NOT FOUND ITEM (ADD NEW ITEM)
      if (_item?.Name !== 'Regular') {
        name += ` (${_item.Name})`
      }


      let mergedItem: any = {
        parentId: options?.parentItem?.Id,
        name: name,
        price: _item.Price || 0,
        extPrice: _item.Price || 0,
        quantity: 1,
        subItemId: _item.Id,
        toppings: options.toppings || [],
        comments: options.comments || "",
        isOpenMenu: options.isOpenMenu || false,
      }
      if (options.openMenuName) mergedItem.openMenuName = options.openMenuName

      // Item-level default discount (mode 0 = percent, 1 = fixed baht). When the
      // menu Item carries a non-zero DiscountValue, auto-apply it to the cart line.
      // Downstream billing (OrderItemHelper → discountLineFn/calculateBilling)
      // computes the amount — we only carry the request here.
      const defaultDiscountValue = Number(options?.parentItem?.DiscountValue) || 0
      if (defaultDiscountValue) {
        mergedItem.discountrequest = {
          mode: Number(options?.parentItem?.DiscountMode) || 0,
          value: defaultDiscountValue,
        }
      }

      const newKey = utilsItemGenKeyInCart(mergedItem)

      // A line marked oneLine (produced by a split) must never absorb a new add —
      // it exists precisely because the cashier wanted it kept apart.
      const found = this.items.find((item) => !item.oneLine && utilsItemGenKeyInCart(item) == newKey)

      //WHEN FOUND ITEM
      if (found) {
        found.quantity++
        return
      }

      this.addItemToCart(mergedItem)

    },
    addItemToCart(item) {
      this.items.push({ ...item, uuid: genCartLineUuid(), })
    },

    removeItemFromCart(index) {
      this.items.splice(index, 1)
    },
    onIncreaseQty(index) {
      this.items[index].quantity++

    },
    onDecreaseQty(index) {
      if (this.items[index].quantity > 1) {
        this.items[index].quantity--

      } else {
        this.removeItemFromCart(index)
      }
    },
    // Set an exact quantity (from the quick-edit dialog). Accepts decimals
    // (e.g. 0.5, 1.5) rounded to 2 places; guards against non-numeric / <= 0.
    setQuantity(index, qty) {
      const n = Number(qty)
      if (!Number.isFinite(n) || n <= 0) return
      this.items[index].quantity = Math.round(n * 100) / 100
    },

    setPrice(item: any, newPrice: number) {
      item.price = newPrice
      item.extPrice = newPrice
    },
    setDiscountItem(item: any, mode: number, value: number) {
      // Manual per-line discount override (mode 0 = percent, 1 = fixed baht PER
      // PIECE — the ×quantity happens at bill-build time, see discountItemLineFn).
      // Clearing to 0 removes the discount so the line no longer applies one.
      const v = Number(value) || 0
      if (v) {
        item.discountrequest = { mode: Number(mode) || 0, value: v }
        delete item.noDefaultDiscount
      } else {
        delete item.discountrequest
        // Mark as intentionally cleared so applyDefaultDiscount (at job-build
        // time) does not silently re-apply the item's default discount.
        item.noDefaultDiscount = true
      }
    },
    // Peel `qty` pieces off a cart line into a NEW line placed right after it.
    // Mirrors the jobchain reducer (JobchainV1-1.split_items): the clone carries
    // everything — price, toppings, comments, discountrequest — so a per-piece
    // baht discount stays correct on both halves without re-computing anything.
    splitCartLine(item: any, qty: number) {
      const idx = this.items.findIndex((line: any) => line.uuid == item.uuid)
      if (idx < 0) return

      const line: any = this.items[idx]
      const total = Number(line.quantity) || 0
      const round2 = (n: number) => Math.round(n * 100) / 100

      const peel = round2(Number(qty) || 0)
      // Reject rather than clamp: a split must leave BOTH lines with a real
      // quantity, and silently peeling a different amount than asked is worse
      // than doing nothing (the editor already caps the input at quantity - 1).
      if (!Number.isFinite(peel) || peel <= 0 || peel >= total) return

      line.quantity = round2(total - peel)
      // BOTH halves must survive as separate rows on the table. Replay merges
      // lines by JobchainV1_1.genItemLineKey, and two halves of the same product
      // key identically — so the split collapsed back into one row on save. The
      // `oneLine` flag makes genItemLineKey key the line by its uniqueid instead
      // (OrderBuilder forwards the flag into the order line).
      line.oneLine = true
      this.items.splice(idx + 1, 0, {
        ..._.cloneDeep(line),
        quantity: peel,
        uuid: genCartLineUuid(),
        oneLine: true,
      })
    },

    // Single entry point for the cart-line editor (Edit → CUSTOMIZE ITEM modal):
    // toppings/comments plus the price / discount / split the modal collects.
    // Order matters — split runs LAST so the new line inherits the fresh values.
    // `price` / `discount` / `splitQty` are null when the modal left them alone.
    updateCartLineFromEditor(
      item: any,
      edits: { price?: number | null; discount?: { mode: number; value: number } | null; splitQty?: number | null } = {}
    ) {
      const found: any = this.items.find((line: any) => line.uuid == item.uuid)
      if (!found) {
        // Add-item flow — the line does not exist in the cart yet.
        this.addItemToCart(item)
        return
      }

      found.toppings = item.toppings
      found.comments = item.comments

      if (edits.price != null) this.setPrice(found, Number(edits.price))
      if (edits.discount) this.setDiscountItem(found, edits.discount.mode, edits.discount.value)
      if (edits.splitQty) this.splitCartLine(found, Number(edits.splitQty))
    },

    clearCart() {
      this.items = []
    },
  },
});

// Cart-line identity. Shared by addItemToCart and splitCartLine so both mint the
// same shape of key.
function genCartLineUuid() {
  return _.random(1, 10000) + ':' + dayjs().valueOf()
}

function utilsItemGenKeyInCart(item, options2 = null) {

  // Include the discount signature so a discounted line does not merge-collide
  // with a non-discounted (or differently-discounted) otherwise-identical line.
  const dcSig = item.discountrequest
    ? `${item.discountrequest.mode || 0}:${item.discountrequest.value || 0}`
    : ''

  return `${item.subItemId}:${item.name}:${item.comments}:${item.price}:${item.toppings?.map((i) => i.toppingId).join(':')}:${dcSig}`
}
